import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchUserProfile, UserProfile } from "../services/profileService";
import { fetchRoleDetails } from "../services/userService";
import { AppLanguage, useLanguage } from "./LanguageContext";

type AuthUserContextValue = {
  user: UserProfile | null;
  permissions: string[];
  isAdmin: boolean;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
  clearUser: () => void;
};

const AuthUserContext = createContext<AuthUserContextValue | undefined>(undefined);

function normalizeProfileLanguage(value?: string): AppLanguage | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "pt-br" || normalized === "pt_br" || normalized === "pt") {
    return "pt-BR";
  }
  if (normalized === "es" || normalized === "es-es") {
    return "es";
  }
  if (normalized === "en" || normalized === "en-us" || normalized === "en-gb") {
    return "en";
  }
  return null;
}

export function AuthUserProvider({ children }: { children: React.ReactNode }) {
  const { language, setLanguage } = useLanguage();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const languageRef = useRef<AppLanguage>(language);
  const hasSyncedLanguageFromProfileRef = useRef(false);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(null);
      setPermissions([]);
      setIsAdmin(false);
      hasSyncedLanguageFromProfileRef.current = false;
      return;
    }

    setIsLoading(true);
    try {
      const profile = await fetchUserProfile();
      setUser(profile);
      const nextIsAdmin =
        (profile?.role || "").trim().toLowerCase() === "administrador";
      setIsAdmin(nextIsAdmin);
      if (nextIsAdmin) {
        setPermissions([]);
      } else if (profile?.id_role) {
        try {
          const details = await fetchRoleDetails({ id_role: profile.id_role });
          setPermissions(details.permissions);
        } catch {
          setPermissions([]);
        }
      } else {
        setPermissions([]);
      }
      const profileLanguage = normalizeProfileLanguage(profile?.preferences?.language);
      if (
        profileLanguage &&
        !hasSyncedLanguageFromProfileRef.current &&
        profileLanguage !== languageRef.current
      ) {
        setLanguage(profileLanguage);
      }
      hasSyncedLanguageFromProfileRef.current = true;
    } catch {
      setUser(null);
      setPermissions([]);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, [setLanguage]);

  const clearUser = useCallback(() => {
    setUser(null);
    setPermissions([]);
    setIsAdmin(false);
    hasSyncedLanguageFromProfileRef.current = false;
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      permissions,
      isAdmin,
      isLoading,
      refreshUser,
      clearUser,
    }),
    [user, permissions, isAdmin, isLoading, refreshUser, clearUser]
  );

  return <AuthUserContext.Provider value={value}>{children}</AuthUserContext.Provider>;
}

export function useAuthUser() {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error("useAuthUser must be used within AuthUserProvider");
  }
  return context;
}
